/**
 * Handles inline FAQ accordion interactions.
 *
 * @export
 * @class InlineFaqs
 */
import { forEach } from '../helpers/utilities';

class InlineFaq {
    private button: HTMLButtonElement;
    private container: HTMLElement;
    private content: HTMLElement;
    private isOpen = false;
    private panel: HTMLElement;
    private resizeHandler: () => void;
    private timeout = 0;
    private transitionDuration: number;

    constructor(container: HTMLElement) {
        this.container          = container;
        this.button             = <HTMLButtonElement>this.container.querySelector('[data-inline-faq-toggle]');
        this.panel              = <HTMLElement>this.container.querySelector('[data-inline-faq-panel]');
        this.content            = <HTMLElement>this.container.querySelector('[data-inline-faq-content]');
        this.transitionDuration = parseFloat(getComputedStyle(this.panel).transitionDuration) * 1000;
        this.resizeHandler      = () => this.onResize();

        this.bindEvents();
    }

    /**
     * Binds events for the FAQ item.
     *
     * @private
     * @memberof InlineFaq
     */
    private bindEvents(): void {
        this.button.addEventListener('click', () => {
            this.toggle();
        });

        window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Opens or closes the FAQ item.
     *
     * @private
     * @memberof InlineFaq
     */
    private toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Opens the FAQ item with a height animation.
     *
     * @private
     * @memberof InlineFaq
     */
    private open(): void {
        window.clearTimeout(this.timeout);
        this.isOpen = true;

        this.container.classList.add('c-inline-faq__item--is-open');
        this.button.setAttribute('aria-expanded', 'true');
        this.panel.setAttribute('aria-hidden', 'false');
        this.panel.style.height = `${this.content.offsetHeight}px`;

        this.timeout = window.setTimeout(() => {
            if (this.isOpen) {
                this.panel.style.height = 'auto';
            }
        }, this.transitionDuration);
    }

    /**
     * Closes the FAQ item with a height animation.
     *
     * @private
     * @memberof InlineFaq
     */
    private close(): void {
        window.clearTimeout(this.timeout);
        this.isOpen = false;

        this.panel.style.height = `${this.content.offsetHeight}px`;

        requestAnimationFrame(() => {
            this.container.classList.remove('c-inline-faq__item--is-open');
            this.button.setAttribute('aria-expanded', 'false');
            this.panel.setAttribute('aria-hidden', 'true');
            this.panel.style.height = '0px';
        });
    }

    /**
     * Keeps open panels stable when content wraps differently after resize.
     *
     * @private
     * @memberof InlineFaq
     */
    private onResize(): void {
        if (this.isOpen) {
            this.panel.style.height = 'auto';
        }
    }
}

export class InlineFaqs {

    constructor() {
        const items = document.querySelectorAll('[data-inline-faq-item]');

        if (items) {
            forEach(items, ({}, item: HTMLElement) => {
                if (item.getAttribute('data-inline-faq-ready') !== 'true') {
                    item.setAttribute('data-inline-faq-ready', 'true');
                    new InlineFaq(item);
                }
            });
        }
    }
}
